import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, of } from 'rxjs';
import { MerchantApi } from '../apis';
import type { LinkedMerchantRow, MerchantAccount } from '../models';

/** localStorage key for the persisted logo cache. Logos are non-sensitive. */
const STORAGE_KEY = 'gozem_account_logos';

/**
 * AccountLogoStore — per-account merchant logo cache for the account switcher.
 *
 * Why this exists: the switcher lists *every* account, but the accounts carried by a session
 * payload have no logo — only the **active** merchant's `logo_url` reaches the app, via the
 * `details_v1` call the auth initializer makes on bootstrap. This store fills that gap for the
 * other rows with one call to `get_linked_merchant`, the same endpoint the Android merchant app
 * uses for the same list.
 *
 * Design (decisions locked with product):
 * - **Lazy**: `hydrate()` is called when the switcher opens, not on every page load — the cost
 *   is bounded to users who actually use the switcher.
 * - **Cached + persisted**: successful logos are kept in memory and mirrored to localStorage
 *   (non-sensitive), so they survive navigation and reload and never refetch.
 * - **One request, no fallback**: a single aggregate covers every account. If it fails or comes
 *   back empty, the switcher keeps its coloured-initials avatars; there is deliberately no
 *   per-account retry. It calls `MerchantApi` directly and writes only here, so it never touches
 *   the active `MerchantStore`.
 *
 * This is the frontend bridge (Option 2). If the backend later adds `logo` to the accounts list
 * in the session payload, `MerchantAccount.logo` wins in the resolver and this store goes dormant.
 *
 * Cleared on logout so one user never sees another's logos.
 */
@Injectable({ providedIn: 'root' })
export class AccountLogoStore {
  private readonly merchantApi = inject(MerchantApi);
  private readonly destroyRef = inject(DestroyRef);

  /** `accountId` → raw image path as returned by the API (unprefixed by the S3 base). */
  private readonly _logos = signal<Record<string, string>>(this.readCache());
  readonly logos = this._logos.asReadonly();

  /** Whether the one request has been issued this session (success or not) — no refetch storms. */
  private attempted = false;

  /** Raw logo path for an account, or '' when unknown. */
  logoFor(accountId: string): string {
    return this._logos()[accountId] ?? '';
  }

  /**
   * Fetch the logos we don't have yet. Safe to call on every dropdown open — it issues at most
   * one request per session, and none at all when every account already has a picture (a logo on
   * the account row itself, or a cached entry).
   *
   * An account with no `merchantId` can never match a response row, so it is not counted as a
   * reason to make the call.
   */
  hydrate(accounts: readonly MerchantAccount[]): void {
    if (this.attempted) return;

    const needsLogo = accounts.some(
      (a) => a.accountId && a.merchantId && !a.logo && !this.logoFor(a.accountId),
    );
    if (!needsLogo) return;

    this.attempted = true;

    // Response rows are keyed by MERCHANT id; the store is keyed by account id.
    const accountIdByMerchantId = new Map(
      accounts.filter((a) => a.accountId && a.merchantId).map((a) => [a.merchantId, a.accountId]),
    );

    this.merchantApi
      .getLinkedMerchants()
      .pipe(
        // A failure just leaves the coloured avatars in place — there is no per-account retry.
        catchError(() => of({ success: false, data: [] })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        const found: Record<string, string> = {};
        (response?.data ?? []).forEach((row: LinkedMerchantRow) => {
          const accountId = accountIdByMerchantId.get(String(row?._id ?? ''));
          // Android parity (`Merchant.getMerchantLogoOrImageUrl`): the brand logo first, the
          // merchant's picture as a second choice.
          const logo = row?.merchant?.logo_url || row?.merchant?.image_url || '';
          if (accountId && logo) found[accountId] = logo;
        });
        this.setLogos(found);
      });
  }

  /** Discard every cached logo (called on logout). */
  clear(): void {
    this.attempted = false;
    this._logos.set({});
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Storage unavailable (private mode / blocked) — the in-memory reset is enough.
    }
  }

  // ── Private ──────────────────────────────────────────────────────────────────

  /** Merge a batch of freshly resolved logos, persisting the cache once rather than per row. */
  private setLogos(logos: Record<string, string>): void {
    if (Object.keys(logos).length === 0) return;
    this._logos.update((current) => ({ ...current, ...logos }));
    this.writeCache(this._logos());
  }

  private readCache(): Record<string, string> {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed: unknown = raw ? JSON.parse(raw) : null;
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, string>) : {};
    } catch {
      return {};
    }
  }

  private writeCache(map: Record<string, string>): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
    } catch {
      // Non-fatal: logos simply won't persist across reloads in this environment.
    }
  }
}
